import { addListener, type AnyAction, createListenerMiddleware, isAnyOf, type TypedAddListener, type TypedStartListening } from '@reduxjs/toolkit';
import { setContext } from '@alltrails/context/slices/contextSlice';
import { updateLocationResult } from '@alltrails/explore-service/redux/exploreServiceSlice';
import { setListsAndListItems } from '@alltrails/lists/slices/lists';
import type ListItem from '@alltrails/lists/types/ListItem';
import getListsAndListItems from '@alltrails/lists/utils/requests';
import {
  clearViewingWaypointId,
  clearWaypointEditingState,
  removeWaypoint,
  selectCustomRouteWaypointById,
  setViewingWaypointId,
  updateWaypointDraft,
  updateWaypointPosition
} from '@alltrails/maps/redux/customRoute';
import {
  clearSelectedWaypoint,
  setSelectedWaypoint,
  toggleStackedOverlayLegend,
  updateNonTrailListResults,
  updateSelectedPOI,
  updateTrailResults
} from '@alltrails/maps/redux/reducer';
import type { NonTrailListResult } from '@alltrails/maps/redux/reducer';
import { isConditionStyleId } from '@alltrails/maps/utils/overlayStack';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import { writeStoredAdvancedMapping } from '@/components/AdvancedMapping/advancedMappingStorage';
import type { AppDispatch, RootState } from './redux';
import {
  clearElevationChartZoom,
  clearSelectedTrail,
  closeRouteDetailsEditor,
  closeSegmentEditor,
  openRouteDetailsEditor,
  openSegmentEditor,
  setExcludedPoiGroups,
  setSelectedTrail,
  toggleContentVisibility,
  toggleGradeShading
} from './slices/advancedMappingSlice';
import { setConditionsPanelOpen, setListPageItems, upsertListPageItem } from './slices/exploreSlice';

export const listenerMiddleware = createListenerMiddleware();

export type AppStartListening = TypedStartListening<RootState, AppDispatch>;

export const startAppListening = listenerMiddleware.startListening as AppStartListening;

export const addAppListener = addListener as TypedAddListener<RootState, AppDispatch>;

const onUserLogin = async (action, listenerApi) => {
  if (action.payload.currentUser) {
    const userLists = await getListsAndListItems();
    listenerApi.dispatch(setListsAndListItems(userLists)); // dispatch lists info
  }
};

const onListMapItemsChange = ({ payload: listItems }: { payload: ListItem[] }, { dispatch }) => {
  const trailResults = listItems
    .filter(item => item.type === 'trail' && item.object?._geoloc)
    .map(item => ({ ...item.object, ID: item.objectId }) as TrailSearchResult);
  dispatch(updateTrailResults(trailResults));

  const nonTrailResults = listItems.flatMap<NonTrailListResult>(item => {
    if (item.type !== 'map' && item.type !== 'track') {
      return [];
    }
    const object = item.object;
    if (!object?._geoloc) {
      return [];
    }
    return [
      {
        ID: item.objectId,
        _geoloc: object._geoloc,
        name: object.name,
        slug: object.slug,
        type: item.type,
        meters: object.meters ?? object.length,
        durationMinutes: object.duration_minutes ?? object.minutes,
        elevationGain: item.type === 'track' ? item.object?.elevation_gain : undefined,
        activityType: item.type === 'track' ? item.object?.activity_type : undefined,
        createdAt: item.metadata?.created
      }
    ];
  });
  dispatch(updateNonTrailListResults(nonTrailResults.length ? nonTrailResults : undefined));
};

const onLatestListActionChange = (_action, { getState, dispatch }) => {
  const state = getState() as RootState;
  const latestListAction = state.lists.latestListAction;
  if (!latestListAction) {
    return;
  }

  if (!state.explore.listPageInfo) {
    return;
  }

  const { listItem, action } = latestListAction;

  if (action === 'remove') {
    if (listItem.type === 'trail') {
      const remaining = Object.values(state.map.trailResults ?? {})
        .flat()
        .filter(t => t.ID !== listItem.objectId);
      dispatch(updateTrailResults(remaining));
    } else {
      const remaining = (state.map.nonTrailListResults ?? []).filter(r => r.ID !== listItem.objectId);
      dispatch(updateNonTrailListResults(remaining.length ? remaining : undefined));
    }
  } else if (action === 'add') {
    if (listItem.object) {
      const now = new Date().toISOString();
      dispatch(
        upsertListPageItem({
          ...listItem,
          metadata: {
            created: listItem.metadata?.created ?? now,
            updated: listItem.metadata?.updated ?? now,
            status: listItem.metadata?.status ?? 'active'
          }
        })
      );
    }
  }
  if (action === 'add' && listItem.object?._geoloc) {
    if (listItem.type === 'trail') {
      const current = Object.values(state.map.trailResults ?? {}).flat();
      if (!current.some(t => t.ID === listItem.objectId)) {
        dispatch(updateTrailResults([...current, { ...listItem.object, ID: listItem.objectId } as unknown as TrailSearchResult]));
      }
    } else {
      const object = listItem.object;
      if (object && '_geoloc' in object) {
        const current = state.map.nonTrailListResults ?? [];
        if (!current.some(r => r.ID === listItem.objectId)) {
          dispatch(
            updateNonTrailListResults([
              ...current,
              {
                ID: listItem.objectId,
                _geoloc: object._geoloc,
                name: object.name,
                slug: object.slug,
                type: listItem.type,
                meters: 'meters' in object ? object.meters : undefined,
                durationMinutes: 'duration_minutes' in object ? object.duration_minutes : undefined
              } as NonTrailListResult
            ])
          );
        }
      }
    }
  }
};

/**
 * Mutual exclusion between Redux fields that "claim" the map sidebar, or advanced mapping's map window.
 *
 * Several independent Redux fields each switch the explore sidebar to a different view (`selectedWaypoint`, `selectedPOI`,
 * `viewingWaypointId`), and the advanced map's windows take the same slot (`selectedTrail`, `isEditingSegment`,
 * `isEditingRouteDetails`, and the route waypoint editor, `isEditingWaypoint`). This listener is the one place that keeps
 * only one of them active: when any claims the slot, the others are released. A new claim needs an entry in
 * `getActionClaim` (or its own transition listener, like the waypoint editor's) and in `releaseClaims`.
 *
 * Releasing the waypoint editor cancels the edit, as its Cancel does: a new waypoint is removed and a dragged one moved
 * back. Explore never gets there, since its claim sites gate their own dispatches while a waypoint edit is open.
 *
 * Longer-term: consolidate these fields into a single tagged-union `mapSidebarOwner` on the map slice; this listener is the
 * migration bridge.
 */
type SidebarClaim =
  'routeDetailsEditor' | 'segmentEditor' | 'selectedPOI' | 'selectedTrail' | 'selectedWaypoint' | 'viewingWaypoint' | 'waypointEditor';

const getActionClaim = (action: AnyAction): SidebarClaim | undefined => {
  if (setSelectedWaypoint.match(action)) {
    return 'selectedWaypoint';
  }
  if (setViewingWaypointId.match(action)) {
    return 'viewingWaypoint';
  }
  if (updateSelectedPOI.match(action) && action.payload !== undefined) {
    return 'selectedPOI';
  }
  if (setSelectedTrail.match(action)) {
    return 'selectedTrail';
  }
  if (openSegmentEditor.match(action)) {
    return 'segmentEditor';
  }
  if (openRouteDetailsEditor.match(action)) {
    return 'routeDetailsEditor';
  }
  return undefined;
};

// Undoes what a waypoint edit changed in the store, from the state it was made in: its draft, a new waypoint, and a drag.
// The form's own fields go with the form.
const revertWaypointEdit = (dispatch: AppDispatch, state: RootState) => {
  const { selectedWaypointId, waypointEditStartPosition } = state.customRoute;
  dispatch(updateWaypointDraft(null));
  if (selectedWaypointId !== undefined) {
    const waypoint = selectCustomRouteWaypointById(state, selectedWaypointId);
    if (waypoint?.isTemporary) {
      dispatch(removeWaypoint(selectedWaypointId));
    } else if (
      waypoint &&
      waypointEditStartPosition?.id === selectedWaypointId &&
      (waypoint.latitude !== waypointEditStartPosition.latitude || waypoint.longitude !== waypointEditStartPosition.longitude)
    ) {
      dispatch(
        updateWaypointPosition({
          id: selectedWaypointId,
          latitude: waypointEditStartPosition.latitude,
          longitude: waypointEditStartPosition.longitude
        })
      );
    }
  }
};

const cancelWaypointEdit = (dispatch: AppDispatch, state: RootState) => {
  revertWaypointEdit(dispatch, state);
  dispatch(clearWaypointEditingState());
};

const releaseClaims = (claim: SidebarClaim, dispatch: AppDispatch, state: RootState) => {
  if (claim !== 'selectedWaypoint' && state.map.selectedWaypoint !== undefined) {
    dispatch(clearSelectedWaypoint());
  }
  if (claim !== 'selectedPOI' && state.map.selectedPOI !== undefined) {
    // "Explore this spot" set the search location to the POI; a location the user searched for is left alone
    const { locationResult } = state.exploreService;
    if (locationResult && 'type' in locationResult && locationResult.type === 'poi' && locationResult.ID === state.map.selectedPOI.ID) {
      dispatch(updateLocationResult(undefined));
    }
    dispatch(updateSelectedPOI(undefined));
  }
  if (claim !== 'viewingWaypoint' && state.customRoute.viewingWaypointId !== undefined) {
    dispatch(clearViewingWaypointId());
  }
  if (claim !== 'selectedTrail' && state.advancedMapping.selectedTrail !== undefined) {
    dispatch(clearSelectedTrail());
  }
  if (claim !== 'segmentEditor' && state.advancedMapping.isEditingSegment) {
    dispatch(closeSegmentEditor());
  }
  if (claim !== 'routeDetailsEditor' && state.advancedMapping.isEditingRouteDetails) {
    dispatch(closeRouteDetailsEditor());
  }
  if (claim !== 'waypointEditor' && state.customRoute.isEditingWaypoint) {
    cancelWaypointEdit(dispatch, state);
  }
};

export const setupListeners = () => {
  startAppListening({ actionCreator: setContext, effect: onUserLogin });
  startAppListening({ actionCreator: setListPageItems, effect: onListMapItemsChange });
  startAppListening({
    predicate: (_action, currentState, previousState) =>
      (currentState as RootState).lists.latestListAction !== (previousState as RootState).lists.latestListAction,
    effect: onLatestListActionChange
  });
  startAppListening({
    predicate: action => getActionClaim(action) !== undefined,
    effect: (action, { dispatch, getState }) => {
      const claim = getActionClaim(action);
      if (claim) {
        releaseClaims(claim, dispatch, getState());
      }
    }
  });
  // Several actions start a waypoint edit (Edit, adding one, setEditState), so the claim is the flag turning on. Starting
  // another while one is open leaves the flag on and switches the waypoint, which cancels the edit it replaces.
  startAppListening({
    predicate: (_action, currentState, previousState) => {
      const current = (currentState as RootState).customRoute;
      const previous = (previousState as RootState).customRoute;
      return Boolean(current.isEditingWaypoint) && (!previous.isEditingWaypoint || current.selectedWaypointId !== previous.selectedWaypointId);
    },
    effect: (_action, { dispatch, getOriginalState, getState }) => {
      const originalState = getOriginalState();
      if (!originalState.customRoute.isEditingWaypoint) {
        releaseClaims('waypointEditor', dispatch, getState());
        return;
      }
      // A new waypoint placed for an add only selects itself; there was no waypoint to cancel before it
      if (originalState.customRoute.selectedWaypointId !== undefined) {
        revertWaypointEdit(dispatch, originalState);
      }
    }
  });
  startAppListening({
    actionCreator: toggleStackedOverlayLegend,
    effect: ({ payload }, { dispatch, getState }) => {
      const { explore, map } = getState();
      if (
        isConditionStyleId(payload) &&
        map.isStackedOverlayLegendOpen &&
        map.dynamicStyleIds.includes(payload) &&
        explore.currentTrail &&
        !explore.conditionsPanelOpen
      ) {
        dispatch(setConditionsPanelOpen(true));
      }
    }
  });
  startAppListening({
    matcher: isAnyOf(setExcludedPoiGroups, toggleContentVisibility, toggleGradeShading),
    effect: (_action, { getState }) => {
      writeStoredAdvancedMapping(getState().advancedMapping);
    }
  });
  // A chart zoom is a range of route distances dragged against one selection, so it is dropped when either moves: the
  // selection, or the segments the distances are measured along (an edit, undo, a reorder, or the metadata that lands
  // after one and reassigns every distance). Clearing on the change itself covers every action that makes it without
  // listing them.
  startAppListening({
    predicate: (_action, currentState, previousState) => {
      const current = currentState as RootState;
      const previous = previousState as RootState;
      return (
        current.advancedMapping.elevationChartZoom !== undefined &&
        (current.customRoute.activeSegmentIndex !== previous.customRoute.activeSegmentIndex ||
          current.customRoute.route.segments !== previous.customRoute.route.segments)
      );
    },
    effect: (_action, { dispatch }) => {
      dispatch(clearElevationChartZoom());
    }
  });
  // The segment editor edits the selected segment, so it closes once nothing is selected: a deselect, the segment's
  // removal, or an undo past its selection. Watching the selection covers every action that clears it without listing them.
  startAppListening({
    predicate: (_action, currentState) => {
      const current = currentState as RootState;
      return Boolean(current.advancedMapping.isEditingSegment) && current.customRoute.activeSegmentIndex === undefined;
    },
    effect: (_action, { dispatch }) => {
      dispatch(closeSegmentEditor());
    }
  });
  // DataDome auto-refreshes the page once a captcha is passed. While a user is building or editing a
  // custom route that refresh would discard their unsaved work, so we opt out of it whenever editing
  // is active and restore the default behavior once editing ends.
  startAppListening({
    predicate: (_action, currentState, previousState) =>
      (currentState as RootState).customRoute.isEditing !== (previousState as RootState).customRoute.isEditing,
    effect: (_action, { getState }) => {
      if (typeof window === 'undefined') {
        return;
      }

      window.ddoptions = window.ddoptions ?? {};
      window.ddoptions.disableAutoRefreshOnCaptchaPassed = !!getState().customRoute.isEditing;
    }
  });
};
